import { useCallback, useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "kw_cache_version";
// Don't refetch more often than once every 5 s — covers rapid tab switches
// without hammering the API.
const MIN_REFETCH_INTERVAL_MS = 5_000;

/**
 * Fetches the global site cache version from the backend.
 * Used by `usePortfolioImages`, `SiteContentContext` and other client caches
 * to namespace their localStorage keys — when the version bumps (auto-daily
 * or after any admin mutation), every visitor's cached data is invalidated
 * on next read.
 *
 * Live refresh: re-checks the server every time the tab regains focus or
 * the document becomes visible again, so already-open browser tabs catch
 * admin updates without requiring a manual refresh.
 *
 * Returns the version (number) or null while loading. Components that
 * depend on it should not block rendering — caches simply fall back to
 * "no cache" until the version is known.
 */
export function useCacheVersion() {
  const [version, setVersion] = useState(() => {
    if (typeof window === "undefined") return null;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v ? Number(v) : null;
  });

  const fetchVersion = useCallback(async () => {
    try {
      // Bypass HTTP cache explicitly — the endpoint already sends
      // Cache-Control: no-store, but be defensive against stubborn CDNs.
      const r = await fetch(`${BACKEND}/api/cache-version`, { cache: "no-store" });
      if (!r.ok) return;
      const j = await r.json();
      if (!j?.version) return;
      setVersion((prev) => {
        if (j.version === prev) return prev;
        try {
          window.localStorage.setItem(STORAGE_KEY, String(j.version));
        } catch {
          /* quota / private — silent */
        }
        return j.version;
      });
    } catch {
      /* offline — keep previous version */
    }
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  // Re-check the server whenever the tab regains focus or visibility.
  // We throttle to once every MIN_REFETCH_INTERVAL_MS to avoid hammering
  // the API on rapid tab switches.
  useEffect(() => {
    if (typeof document === "undefined") return;
    let lastFetchAt = Date.now();
    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastFetchAt < MIN_REFETCH_INTERVAL_MS) return;
      lastFetchAt = now;
      if (document.visibilityState !== "hidden") {
        fetchVersion();
      }
    };
    document.addEventListener("visibilitychange", maybeRefresh);
    window.addEventListener("focus", maybeRefresh);
    return () => {
      document.removeEventListener("visibilitychange", maybeRefresh);
      window.removeEventListener("focus", maybeRefresh);
    };
  }, [fetchVersion]);

  return version;
}
